import { useEffect, useRef, useState } from 'react';

/** Reveal pace when only a little text is waiting: a calm, readable typing speed. */
const BASE_CHARS_PER_SEC = 60;
/** Any backlog is drained within roughly this long, so a burst speeds the reveal up. */
const CATCH_UP_SECS = 1.5;
/** Ceiling on the reveal speed, so a very large burst still animates rather than jumps. */
const MAX_CHARS_PER_SEC = 4000;
/**
 * Minimum time between rendered updates. The reveal advances every animation frame, but each
 * render re-parses the whole reply as markdown and the caller re-measures scroll height, so
 * committing at 60fps is wasted work on a long reply. ~20 updates/s still reads as smooth.
 */
const MIN_RENDER_INTERVAL_MS = 50;

const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const nextFrame = (cb: (now: number) => void): number =>
    (typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(cb)
        : window.setTimeout(() => cb(Date.now()), 16));

const cancelFrame = (id: number) => {
    if (typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(id);
    } else {
        window.clearTimeout(id);
    }
};

/**
 * Returns a prefix of `text` that grows towards the full value at a steady pace.
 *
 * Chunks arrive from the agent in uneven bursts (several at once, then a pause). Rendering
 * each burst as it lands makes the reply jump; this decouples arrival from display. The reveal
 * speed adapts to the backlog — slow when little is waiting, faster after a burst — so the
 * display never trails the stream by more than about CATCH_UP_SECS.
 *
 * Display-only: the caller keeps the full text in state, so nothing received can be lost.
 * A message that mounts with its text already present (history, or a non-streaming reply)
 * is shown in full immediately. If `text` changes to something that does not extend what is
 * shown (e.g. a transient notice replaced by the real answer), the reveal restarts.
 */
export default function useSmoothText(text: string): string {
    const [shown, setShown] = useState(text);
    const shownRef = useRef(text);
    const targetRef = useRef(text);
    const frameRef = useRef<number | null>(null);
    const lastTickRef = useRef(0);
    const carryRef = useRef(0);
    const lastRenderRef = useRef(0);

    targetRef.current = text;

    useEffect(() => {
        if (prefersReducedMotion()) {
            shownRef.current = text;
            setShown(text);
            return;
        }

        if (!text.startsWith(shownRef.current)) {
            // Not a continuation of what is on screen: start the reveal over.
            shownRef.current = '';
            setShown('');
        }

        if (shownRef.current.length >= text.length || frameRef.current != null) {
            return;
        }

        const tick = (now: number) => {
            const target = targetRef.current;
            const dt = lastTickRef.current ? Math.min((now - lastTickRef.current) / 1000, 0.1) : 0;
            lastTickRef.current = now;

            const backlog = target.length - shownRef.current.length;
            if (backlog <= 0) {
                frameRef.current = null;
                lastTickRef.current = 0;
                carryRef.current = 0;
                return;
            }

            const speed = Math.min(MAX_CHARS_PER_SEC, Math.max(BASE_CHARS_PER_SEC, backlog / CATCH_UP_SECS));
            carryRef.current += speed * dt;
            const step = Math.floor(carryRef.current);
            if (step > 0) {
                carryRef.current -= step;
                let end = Math.min(target.length, shownRef.current.length + step);
                // Never split a UTF-16 surrogate pair (emoji, some symbols).
                const code = target.charCodeAt(end - 1);
                if (end < target.length && code >= 0xd800 && code <= 0xdbff) {
                    end += 1;
                }
                shownRef.current = target.slice(0, end);
                // Always commit once the reveal catches up, so the final text is never held back.
                const caughtUp = end >= target.length;
                if (caughtUp || now - lastRenderRef.current >= MIN_RENDER_INTERVAL_MS) {
                    lastRenderRef.current = now;
                    setShown(shownRef.current);
                }
            }
            frameRef.current = nextFrame(tick);
        };

        frameRef.current = nextFrame(tick);
    }, [text]);

    useEffect(() => () => {
        if (frameRef.current != null) {
            cancelFrame(frameRef.current);
        }
    }, []);

    return shown;
}
